import {
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import { Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { listAllUsers } from '../../../modules/users';
import CustomButton from '../../elements/CustomButton';
import TextInput from '../../elements/TextInput';

const UserProjectForm = ({ initialValues, onSubmit, type }) => {
  const [showDefaultAssignee, setShowDefaultAssignee] = useState(
    !initialValues.mandatoryAssignedTo || false
  );
  const [projectUsers, setProjectUsers] = useState([]);
  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required('Project name is required.')
      .test(
        'has-letter',
        'Project name must contain at least one letter.',
        (value) => /[a-zA-Z]/.test(value)
      )
      .test(
        'valid-characters',
        'Project name can only include letters, numbers, spaces, or hyphens (-).',
        (value) => /^[a-zA-Z0-9\s-]*$/.test(value)
      )
      .min(1, 'Project name must be at least 1 character long.'),
    description: Yup.string(),
  });

  const fetchProjectUsers = async () => {
    try {
      const usersList = await listAllUsers();
      setProjectUsers(usersList?.users);
    } catch (error) {
      console.log('Failed to fetch projects. Please try again.');
    }
  };

  useEffect(() => {
    fetchProjectUsers();
  }, []);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}>
      {({ errors, touched, handleSubmit, values, setFieldValue }) => {
        const handleChange = (field, value) => {
          setFieldValue(field, value);
        };

        return (
          <Form noValidate onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <InputLabel>Name</InputLabel>
                <TextInput
                  id="name"
                  required
                  value={values.name}
                  onChange={handleChange}
                  placeholder="Enter Project Name"
                  maxLength={50}
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                />
              </Grid>

              <Grid item xs={12}>
                <InputLabel>Description</InputLabel>
                <TextInput
                  id="description"
                  value={values.description}
                  onChange={handleChange}
                  maxLength={255}
                  placeholder="Enter Description"
                  type="textarea"
                  rows={3}
                  error={touched.description && Boolean(errors.description)}
                  helperText={touched.description && errors.description}
                />
              </Grid>
              <Grid item xs={12}>
                <InputLabel>
                  Do you want to add a test case review workflow?
                </InputLabel>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    name="reviewWorkflow"
                    value={values.reviewWorkflow}
                    onChange={(e) =>
                      setFieldValue('reviewWorkflow', e.target.value === 'true')
                    }>
                    <FormControlLabel
                      value="true"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="false"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <InputLabel>
                  Do you want to enable mandatory assignment of test cases
                  within a test cycle?
                </InputLabel>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    name="mandatoryAssignedTo"
                    value={values.mandatoryAssignedTo}
                    onChange={(e) => {
                      const isMandatory = e.target.value === 'true';
                      setFieldValue('mandatoryAssignedTo', isMandatory);
                      setShowDefaultAssignee(!isMandatory);
                    }}>
                    <FormControlLabel
                      value="true"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="false"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {showDefaultAssignee && (
                <Grid item xs={4}>
                  <InputLabel>Default Assignee</InputLabel>
                  <TextField
                    select
                    id="defaultAssignee"
                    name="defaultAssignee"
                    fullWidth
                    variant="outlined"
                    onChange={(e) =>
                      setFieldValue('defaultAssignedTo', e.target.value)
                    }
                    value={values.defaultAssignedTo || ''}
                    error={
                      touched.defaultAssignedTo &&
                      Boolean(errors.defaultAssignedTo)
                    }
                    helperText={
                      touched.defaultAssignedTo && errors.defaultAssignedTo
                    }>
                    {projectUsers?.map((member) => (
                      <MenuItem key={member.id} value={member.id}>
                        {member.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
            </Grid>

            <CustomButton buttonLabel={type === 'edit' ? 'Save Changes' : 'Create Project'} />
          </Form>
        );
      }}
    </Formik>
  );
};

export default UserProjectForm;
